import { clsx } from 'clsx'
import { type FC, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  OfferDiscountTypeEnumV2,
  type OfferV11,
  type PrescriptionPrescriptionTypeEnum,
} from '@redteclab/api/clients/bully'

import { Rating } from 'base-ui'

import {
  FEATURE_TOGGLE_VOLUME_BASED_PRICES,
  useFeatureToggle,
} from '../../../../../feature-toggles'
import { priceGetFormattedPrice } from '../../../../../price'
import {
  productAccessorGetBestSellerPromotionFromOffer,
  ProductSellerVoucherLabel,
} from '../../../../../product'
import { offerAccessorOfferHasVolumePricing } from '../../../../model/offerAccessor/offerAccessorOfferHasVolumePricing'
import { priceAccessorGetDisplayPrice } from '../../../../model/priceAccessor/priceAccessorGetDisplayPrice'
import { priceAccessorGetFormattedUnitPrice } from '../../../../model/priceAccessor/priceAccessorGetFormattedUnitPrice'
import { productAccessorHasBestOfferFlag } from '../../../../model/productAccessor/productAccessorHasBestOfferFlag'
import { productAccessorOfferHasSellerPromotion } from '../../../../model/productAccessor/productAccessorOfferHasSellerPromotion'
import { shippingAccessorGetFormattedShippingLeadTime } from '../../../../model/shippingAccessor/shippingAccessorGetFormattedShippingLeadTime'
import { shippingAccessorGetFormattedShippingTypeCost } from '../../../../model/shippingAccessor/shippingAccessorGetFormattedShippingTypeCost'
import { shippingAccessorGetShippingByPriority } from '../../../../model/shippingAccessor/shippingAccessorGetShippingByPriority'

import { ProductOfferCardSellerPromotionLabel } from './ProductOfferCardSellerPromotionLabel'
import { ProductOfferGoToProductCta } from './ProductOfferGoToProductCta'
import { ProductOfferSellerLink } from './ProductOfferSellerLink'
import { ProductOfferVolumePricingLabel } from './ProductOfferVolumePricingLabel'

type ProductOfferCardProps = {
  offer?: OfferV11
  prescriptionType: PrescriptionPrescriptionTypeEnum
}

const getSellerPromotionLabel = (offer: OfferV11): ReactNode | null => {
  const sellerPromotion = productAccessorGetBestSellerPromotionFromOffer(offer)

  if (
    sellerPromotion?.voucherCode &&
    sellerPromotion.discountType === OfferDiscountTypeEnumV2.TotalPercent
  ) {
    return <ProductSellerVoucherLabel sellerPromotion={sellerPromotion} />
  }

  if (!productAccessorOfferHasSellerPromotion(offer)) {
    return null
  }

  return <ProductOfferCardSellerPromotionLabel offer={offer} />
}

const getOfferLabels = ({
  isVolumePricingFeatureEnabled,
  offer,
}: {
  isVolumePricingFeatureEnabled: boolean
  offer: OfferV11
}): ReactNode | null => {
  const labels = []

  if (productAccessorHasBestOfferFlag(offer)) {
    labels.push(
      <span
        className="flex items-center rounded-full border-dark-tertiary bg-light-tertiary px-2.5 py-1 text-xs"
        data-qa-id="offer-label-best-offer"
      >
        <FormattedMessage id="productOffer.cheapest" />
      </span>,
    )
  }

  if (
    isVolumePricingFeatureEnabled &&
    offerAccessorOfferHasVolumePricing(offer)
  ) {
    labels.push(
      <ProductOfferVolumePricingLabel
        className="border-none bg-light-tertiary px-3.5 py-1"
        currentOffer={offer}
      />,
    )
  }

  const sellerPromotionLabel = getSellerPromotionLabel(offer)
  if (sellerPromotionLabel) {
    labels.push(sellerPromotionLabel)
  }

  return labels.length > 0 ? (
    <div className="absolute -top-3.5 flex flex-row items-start gap-2">
      {labels}
    </div>
  ) : null
}

export const ProductOfferCard: FC<ProductOfferCardProps> = ({
  offer,
  prescriptionType,
}) => {
  const intl = useIntl()

  const isVolumePricingFeatureEnabled = useFeatureToggle(
    FEATURE_TOGGLE_VOLUME_BASED_PRICES,
  )

  if (!offer) {
    return null
  }

  const currentDelivery = shippingAccessorGetShippingByPriority(offer)
  const currentPrice = priceAccessorGetDisplayPrice(offer, prescriptionType)

  const isFreeShipping =
    typeof currentDelivery?.shippingFreeAmount === 'number' &&
    typeof currentPrice?.price === 'number' &&
    currentPrice.price >= currentDelivery.shippingFreeAmount

  const shippingPriceByType = currentDelivery
    ? shippingAccessorGetFormattedShippingTypeCost({
        intl,
        isFreeShipping,
        shippingTypeCode: currentDelivery.shippingTypeCode,
        totalShippingPrice: currentDelivery.totalShippingPrice,
      })
    : ''

  const labelsBlock = getOfferLabels({
    isVolumePricingFeatureEnabled,
    offer,
  })

  return (
    <div
      className={clsx(
        'relative rounded-m bg-light-primary-low px-4 py-6',
        labelsBlock && 'mt-4',
      )}
      data-qa-id={`product-offer-card-${offer.offerId}`}
    >
      {labelsBlock}
      <div className="grid grid-cols-3 justify-between gap-6 tablet:grid-cols-10">
        <div className="col-span-1 flex flex-col justify-center tablet:col-span-2">
          {currentPrice?.price ? (
            <span className="text-xl font-medium">
              {priceGetFormattedPrice(currentPrice.price, intl)}
            </span>
          ) : null}
          <span className="text-dark-primary-strong">
            {priceAccessorGetFormattedUnitPrice(offer, intl)}
          </span>
        </div>
        <div className="col-span-2 flex flex-col justify-center tablet:col-span-3">
          <span className="font-medium text-dark-success">
            {shippingAccessorGetFormattedShippingLeadTime(
              intl,
              currentDelivery?.deliveryTime,
            )}
          </span>
          <span className="text-xs text-dark-primary-strong">
            {shippingPriceByType}
          </span>
          {!isFreeShipping && currentDelivery?.shippingFreeAmount ? (
            <span className="text-xs text-dark-primary-strong">
              <FormattedMessage
                id="buyBox.link.label.belowFreeShippingLimit"
                values={{
                  amount: priceGetFormattedPrice(
                    currentDelivery.shippingFreeAmount,
                    intl,
                  ),
                }}
              />
            </span>
          ) : null}
        </div>
        <div className="col-span-2 flex flex-col justify-center gap-1 tablet:col-span-3">
          <span className="w-3/4 text-xs text-dark-primary-strong">
            <FormattedMessage id="productOffer.soldBy" />
          </span>
          {offer.seller ? (
            <ProductOfferSellerLink seller={offer.seller} />
          ) : null}
          {offer.seller?.sellerGrade && offer.seller.sellerRatingCount ? (
            <span className="flex text-xs text-dark-primary-strong">
              <Rating
                aria-hidden="true"
                className="text-l"
                readOnly
                value={offer.seller.sellerGrade}
              />
              <span className="sr-only">
                {intl.formatMessage(
                  { id: 'productOffer.sellerRatingOutOfFiveStars' },
                  {
                    rating: intl.formatNumber(offer.seller.sellerGrade, {
                      maximumFractionDigits: 1,
                    }),
                  },
                )}
              </span>
              {`(${offer.seller.sellerRatingCount})`}
            </span>
          ) : null}
        </div>
        <div className="col-span-1 flex items-end justify-end tablet:col-span-2 tablet:items-center">
          <ProductOfferGoToProductCta offer={offer} />
        </div>
      </div>
    </div>
  )
}
